import { CSSProperties, FC, Fragment, Suspense, lazy, useMemo } from "react";
import { useId } from "@fluentui/react-hooks";
import { LoadingOverlay } from "cayo.ui";
import { Entity, PropertyAnnotation } from "@cayo/shared/api/types/cayo-graph";
import { IScheme } from "@cayo/shared/api/types/schema";
import { schemeParts } from "@cayo/shared/components/schemes/schemeParts";
import useService from "@cayo/shared/services/hooks/useService";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import { hasOwnKey } from "@cayo/shared/utils/typeUtils";
import GenericScheme from "./GenericScheme";
import LoginScheme from "./LoginScheme";
import ajax from "@cayo/shared/libs/ajax";
import Form from "../Form";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";

const Dashboard = lazy(() => import("../Dashboard"));

const DashboardRenderer: FC<any> = (props) => (
  <Suspense fallback={<LoadingOverlay />}>
    <Dashboard {...props} />
  </Suspense>
);

export type AuxSchemeProps = {
  object?: Entity;
  signals?: IPropertiesPanelSignals;
  propertyAnnotations?: PropertyAnnotation[];
  parentObjectName?: string;
  parentContainerName?: string;
  onSubmit?: (object: Entity) => Promise<void>;
};

export type CommonSchemeLoaderProps = {
  schemePath: string;
  style?: CSSProperties;
  auxProps?: AuxSchemeProps;
};

type SchemeLoaderProps = CommonSchemeLoaderProps & {};

const SchemeLoader: FC<SchemeLoaderProps> = ({ schemePath, style, auxProps }) => {
  const { object, propertyAnnotations, parentObjectName, parentContainerName, signals, onSubmit } =
    auxProps || {};
  const { getIconAnnotation } = useTypeAnnotations();

  const schemeId = useId("scheme");
  const schemeProps = useMemo(() => {
    const isDashboard = schemePath?.toLowerCase().startsWith(schemeParts.ids.dashboard) ?? false;
    const parts = schemePath?.split("/") ?? [];
    const feature = isDashboard && parts.length === 2 ? parts[1] : undefined;

    return {
      id: schemePath || "guardian",
      componentId: isDashboard ? schemeParts.ids.dashboard : schemePath || "guardian",
      feature,
    };
  }, [schemePath]);

  const { data: jsonScheme, isLoading, error } = ajax.useGet("scheme/{id}", schemeProps);

  const actionBuilder = useService("actionBuilder");

  const type = (jsonScheme as IScheme)?.type;
  const isForm = type === "json-form";
  const compProps = useMemo(() => {
    // TODO remove after support CustomView
    if (jsonScheme?.items?.find((i: any) => i.name === "remediationComplexity")) {
      const re = jsonScheme?.items.find((i: any) => i.name === "remediationComplexity");

      re.customView = "RemediationComplexity";
    }

    const Component = hasOwnKey(schemeMap, schemeProps.componentId)
      ? schemeMap[schemeProps.componentId]
      : GenericScheme;

    const Comp = isForm ? Form : Component;

    const isInsidePropPanel = /^(form\/(edit|new|type)|custom|picker)\//.test(schemePath);

    return { Comp, isInsidePropPanel };
  }, [type, schemeProps.componentId, style]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (error) {
    return <Fragment>{error}</Fragment>;
  }

  return (
    <compProps.Comp
      {...jsonScheme}
      key={schemeId || "guardian"}
      actionBuilder={actionBuilder}
      object={object}
      style={style}
      feature={schemeProps.feature}
      propertyAnnotations={propertyAnnotations}
      parentObjectName={parentObjectName}
      parentContainerName={parentContainerName}
      signals={signals}
      onSubmit={isForm ? onSubmit : undefined}
      isInsidePropPanel={compProps.isInsidePropPanel}
      getIconAnnotation={getIconAnnotation}
    />
  );
};

const schemeMap = {
  login: LoginScheme,
  dashboard: DashboardRenderer,
};

export default SchemeLoader;
